import React, { useContext, useState } from 'react';
import { useRouter } from 'next/router';
import SellCatalogService from '@services/sell-service';
import {
  CatalogManagementProduct,
  CatalogManagementData,
  CatalogManagementFilters,
} from '@interfaces/models/sell-catalog';
import { ApiResponse } from '@interfaces/api';
import logger from '@helpers/utils/logger/client';

interface CatListContextProps {
  fetchCatalogListing: () => Promise<ApiResponse<CatalogManagementData>>;
  list: CatalogManagementProduct[];
  setList: React.Dispatch<React.SetStateAction<CatalogManagementProduct[]>>;
  setTotalCMProductsCount: React.Dispatch<React.SetStateAction<undefined | number>>;
  setSelectedListItem: React.Dispatch<React.SetStateAction<CatalogManagementProduct>>;
  totalCMProductsCount: undefined | number;
  selectedListItem: CatalogManagementProduct;
  droppedPvpPrice: string;
  droppedPvnPrice: string;
  catalogData: CatalogManagementData;
  setDroppedPvpPrice: React.Dispatch<React.SetStateAction<string>>;
  setDroppedPvnPrice: React.Dispatch<React.SetStateAction<string>>;
  cmDataLoading: boolean;
  setCMDataLoading: React.Dispatch<React.SetStateAction<boolean>>;
  optionsList: Array<string>;
  fetchFilters: () => Promise<CatalogManagementFilters[]>;
  toggleFiltersCheckedState: (filterIndex, id) => void;
  getAllCheckedFilters: () => CatalogManagementFilters[];

  // New Properties
  activeUniverse: string | string[];
  setActiveUniverse: React.Dispatch<React.SetStateAction<string | string[]>>;
  activeSort: string | string[];
  setActiveSort: React.Dispatch<React.SetStateAction<string | string[]>>;
  searchTerm: string | string[];
  setSearchTerm: React.Dispatch<React.SetStateAction<string | string[]>>;
  rowsPerPage: number;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  activePage: number;
  setActivePage: React.Dispatch<React.SetStateAction<number>>;
  filters: Array<CatalogManagementFilters>;
  setFilters: React.Dispatch<React.SetStateAction<Array<CatalogManagementFilters>>>;
  getListParams: () => string;
  queryParamString: string;
  setQueryParamString: React.Dispatch<React.SetStateAction<string>>;
}

const CatListContext = React.createContext<CatListContextProps>(undefined);

const CatListProvider = (props) => {
  const [list, setList] = useState<CatalogManagementProduct[]>([]);
  const [catalogData, setCatalogData] = useState<CatalogManagementData>();
  const [totalCMProductsCount, setTotalCMProductsCount] = useState(undefined);
  const [droppedPvpPrice, setDroppedPvpPrice] = useState<string>('');
  const [droppedPvnPrice, setDroppedPvnPrice] = useState<string>('');
  const [selectedListItem, setSelectedListItem] = useState<CatalogManagementProduct>(null);
  const router = useRouter();
  const optionsList = ['20', '40', '60', '100'];
  const selectedLimit = router?.query?.limit || optionsList[0];
  const [cmDataLoading, setCMDataLoading] = useState(true);
  const orderedArray = ['state', 'category', 'subcategory', 'brand', 'model', 'material', 'color'];

  // New Properties
  const [activeUniverse, setActiveUniverse] = useState<string | string[]>(router?.query?.universe || '');
  const [activeSort, setActiveSort] = useState<string | string[]>(router?.query?.sortBy || 'default');
  const [searchTerm, setSearchTerm] = useState<string | string[]>(router?.query?.search || '');
  const [rowsPerPage, setRowsPerPage] = useState<number>(Number(router?.query?.limit) || Number(selectedLimit));
  const [activePage, setActivePage] = useState<number>(Number(router?.query?.page) || 1);
  const [filters, setFilters] = useState<CatalogManagementFilters[]>([]);
  const [queryParamString, setQueryParamString] = useState('');

  const handleUniverseChange = (universe: string) => setActiveUniverse(universe);
  const handleSort = (sort: string) => setActiveSort(sort);
  const handleSearch = (term: string) => setSearchTerm(term);
  const handleRowsPerPageChange = (rowsNumber: number) => setRowsPerPage(rowsNumber);
  const handlePageChange = (pageNumber: number) => setActivePage(pageNumber);
  const handleFiltersChange = (filters: CatalogManagementFilters[]) => setFilters(filters);

  const getCheckedItemsInAFilter = (filterIndex) => {
    const results = filters[filterIndex]?.list?.filter((item) => item.checked);
    return results;
  };

  const getAllCheckedFilters = () => {
    const results = [];
    for (let i = 0; i < filters?.length; i++) {
      results.push({ ...filters[i], list: getCheckedItemsInAFilter(i) });
    }
    return results;
  };

  const getListParams = (): string => {
    let queryParamsString = `page=${activePage}`;

    if (rowsPerPage) {
      queryParamsString = `${queryParamsString}&limit=${rowsPerPage}`;
    }

    if (activeUniverse) {
      queryParamsString = `${queryParamsString}&universe=${activeUniverse}`;
    }

    if (activeSort) {
      queryParamsString = `${queryParamsString}&sortBy=${activeSort}`;
    }

    if (searchTerm) {
      queryParamsString = `${queryParamsString}&search=${searchTerm}`;
    }

    getAllCheckedFilters().map((filterItem: CatalogManagementFilters) => {
      if (filterItem?.list?.length) {
        const finalUrlIds = filterItem?.list?.map((list) => list?.id);
        const activeFilterIds = finalUrlIds.join('-');
        queryParamsString = `${queryParamsString}&${filterItem?.filter}=${activeFilterIds}`;
      }
    });

    if (queryParamString) {
      const newPath = `${router?.pathname}/?${queryParamsString}`;
      window.history.pushState(null, '', newPath);
    }

    // Only for status filter (custom handling)
    if (queryParamsString.includes('state=')) {
      const stateSubstring = queryParamsString.substring(
        queryParamsString.indexOf('state='),
        queryParamsString.lastIndexOf('_1') + 2,
      );
      const replacedUrl = stateSubstring.replaceAll('state=', '&').replaceAll('_', '=').replaceAll('-', '&');
      queryParamsString = queryParamsString.replace(stateSubstring, replacedUrl);
      queryParamsString = queryParamsString.replaceAll('&&', '&');
    }

    return queryParamsString;
  };

  const fetchCatalogListing = async () => {
    try {
      setCMDataLoading(true);
      if (!searchTerm) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }
      const queryParamsString = getListParams();
      const response = await SellCatalogService.listingCatalogManagement(queryParamsString);
      setList(response?.data?.products || []);
      setCatalogData(response?.data);
      setTotalCMProductsCount(response?.meta?.count);
      setCMDataLoading(false);
      return response;
    } catch (error) {
      setList([]);
      setTotalCMProductsCount(0);
      setCMDataLoading(false);
      logger.error(error, 'Error while fetch catalog products. Server responded with: ');
    }
  };

  const fetchFilters = async () => {
    try {
      const queryParamsObject = router?.query;
      const allFilters = await SellCatalogService.getAllFilters();
      const brandLists = await SellCatalogService.getBrands();
      allFilters?.forEach((filter) => {
        if (filter?.filter === 'brand') {
          filter.list = brandLists;
        }
        filter?.list?.forEach((item) => {
          item.checked = false;
          if (filter?.filter === 'state') {
            item.id = `${item?.id}_1`;
          }
          if (queryParamsObject[filter?.filter]) {
            String(queryParamsObject[filter?.filter])
              .split('-')
              .map((filterId) => {
                if (item?.id === filterId) {
                  item.checked = true;
                }
              });
          }
        });
        filter?.list?.sort((a, b) => Number(b.checked) - Number(a.checked));
      });
      const sortedFilterArray = [];
      orderedArray.map((filterLabel) => {
        allFilters?.map((filter) => {
          if (filter?.filter === filterLabel) {
            sortedFilterArray.push(filter);
          }
        });
      });

      setFilters(sortedFilterArray);
      return allFilters;
    } catch (error) {
      logger.error(error, 'Error while fetch catalog filters. Server responded with: ');
    }
  };

  const toggleFiltersCheckedState = (filterIndex, filterListId) => {
    const filtersList = [...filters];
    const modifiedFilter = {
      ...filters[filterIndex],
      list: [...filters[filterIndex].list],
    };
    const filterListIndex = filters[filterIndex].list.findIndex((item) => item.id === filterListId);
    if (modifiedFilter) {
      modifiedFilter.list[filterListIndex].checked = !filters[filterIndex]?.list[filterListIndex]?.checked;
      filtersList[filterIndex] = modifiedFilter;
      setFilters(filtersList);
    }
  };

  const value = {
    fetchCatalogListing,
    list,
    setList,
    totalCMProductsCount,
    setTotalCMProductsCount,
    selectedListItem,
    setSelectedListItem,
    droppedPvpPrice,
    setDroppedPvpPrice,
    droppedPvnPrice,
    setDroppedPvnPrice,
    catalogData,
    optionsList,
    filters,
    setFilters,
    fetchFilters,
    toggleFiltersCheckedState,
    getAllCheckedFilters,
    cmDataLoading,
    setCMDataLoading,

    // New Properties
    activeUniverse,
    setActiveUniverse,
    activeSort,
    setActiveSort,
    searchTerm,
    setSearchTerm,
    rowsPerPage,
    setRowsPerPage,
    activePage,
    setActivePage,
    handleFiltersChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    handleSort,
    handleUniverseChange,
    getListParams,
    queryParamString,
    setQueryParamString,
  };

  return <CatListContext.Provider value={value}>{props.children}</CatListContext.Provider>;
};

const useList = () => useContext(CatListContext);

export { useList, CatListProvider };
